import React, { useState, useEffect, useCallback } from 'react';

/* V70.148 — Hızlı Araçlar: Hesap Makinesi.
 * Klasik anlık-işlem (immediate execution) modeli; eval YOK.
 * TR biçim: ondalık virgül, binlik nokta. Klavye destekli; ekrana tıkla → kopyala. */

const OPS = { '+': '+', '-': '-', '−': '-', '*': '*', '×': '*', '/': '/', '÷': '/' };
const OP_SYM = { '+': '+', '-': '−', '*': '×', '/': '÷' };

const round10 = (n) => Math.round(n * 1e10) / 1e10;

const compute = (a, op, b) => {
  if (op === '+') return round10(a + b);
  if (op === '-') return round10(a - b);
  if (op === '*') return round10(a * b);
  if (op === '/') return b === 0 ? null : round10(a / b);
  return b;
};

const fmtNum = (n) =>
  n.toLocaleString('tr-TR', { maximumFractionDigits: 10 });

/* Girilmekte olan ham string'i ('.' ondalıklı) TR görünümüne çevirir */
const fmtEntry = (s) => {
  if (s === '' || s === '-') return s || '0';
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  const [int, frac] = body.split('.');
  const gInt = (int || '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const out = frac !== undefined ? `${gInt},${frac}` : gInt;
  return neg ? `-${out}` : out;
};

const KEYS = [
  { k: 'C', cls: 'ftc-fn ftc-danger' }, { k: '⌫', cls: 'ftc-fn' }, { k: '%', cls: 'ftc-fn' }, { k: '÷', cls: 'ftc-op' },
  { k: '7' }, { k: '8' }, { k: '9' }, { k: '×', cls: 'ftc-op' },
  { k: '4' }, { k: '5' }, { k: '6' }, { k: '−', cls: 'ftc-op' },
  { k: '1' }, { k: '2' }, { k: '3' }, { k: '+', cls: 'ftc-op' },
  { k: '±' }, { k: '0' }, { k: ',' }, { k: '=', cls: 'ftc-eq' },
];

const CalculatorTool = () => {
  const [cur, setCur] = useState('0');   // aktif giriş ('.' ondalıklı ham string)
  const [acc, setAcc] = useState(null);  // biriken değer
  const [op, setOp] = useState(null);    // bekleyen işlem ('+','-','*','/')
  const [fresh, setFresh] = useState(true); // sonraki rakam yeni giriş başlatır
  const [expr, setExpr] = useState('');  // üst önizleme satırı
  const [err, setErr] = useState(false);
  const [copied, setCopied] = useState(false);

  const reset = useCallback(() => { setCur('0'); setAcc(null); setOp(null); setFresh(true); setExpr(''); setErr(false); }, []);

  const digit = useCallback((d) => {
    if (err) { setErr(false); setCur(d); setAcc(null); setOp(null); setExpr(''); setFresh(false); return; }
    if (fresh) {
      if (op === null) setExpr(''); // "=" sonrası yeni sayı → önceki ifade temizlenir
      setCur(d);
      setFresh(false);
      return;
    }
    setCur(c => {
      if (c.replace(/[^0-9]/g, '').length >= 15) return c;
      return c === '0' ? d : c + d;
    });
  }, [err, fresh, op]);

  const dot = useCallback(() => {
    if (err) return;
    if (fresh) { if (op === null) setExpr(''); setCur('0.'); setFresh(false); return; }
    setCur(c => (c.includes('.') ? c : c + '.'));
  }, [err, fresh, op]);

  const applyOp = useCallback((sym) => {
    if (err) return;
    const nextOp = OPS[sym];
    const b = parseFloat(cur) || 0;
    if (op !== null && !fresh) {
      const res = compute(acc, op, b);
      if (res === null || !isFinite(res)) { setErr(true); setExpr(''); return; }
      setAcc(res);
      setCur(String(res));
      setExpr(`${fmtNum(res)} ${OP_SYM[nextOp]}`);
    } else if (acc === null || op === null) {
      setAcc(b);
      setExpr(`${fmtNum(b)} ${OP_SYM[nextOp]}`);
    } else {
      setExpr(`${fmtNum(acc)} ${OP_SYM[nextOp]}`); // ardışık işlem değişikliği
    }
    setOp(nextOp);
    setFresh(true);
  }, [err, cur, op, fresh, acc]);

  const equals = useCallback(() => {
    if (err || op === null || acc === null) return;
    const b = parseFloat(cur) || 0;
    const res = compute(acc, op, b);
    if (res === null || !isFinite(res)) { setErr(true); setExpr(''); setOp(null); setAcc(null); setFresh(true); return; }
    setExpr(`${fmtNum(acc)} ${OP_SYM[op]} ${fmtNum(b)} =`);
    setCur(String(res));
    setAcc(null);
    setOp(null);
    setFresh(true);
  }, [err, op, acc, cur]);

  const percent = useCallback(() => {
    if (err) return;
    const b = parseFloat(cur) || 0;
    // Finans alışkanlığı: 200 + 10 % → 200'ün %10'u (20); × ve ÷ için düz b/100
    const v = op && acc !== null && (op === '+' || op === '-') ? round10((acc * b) / 100) : round10(b / 100);
    setCur(String(v));
    setFresh(false);
  }, [err, cur, op, acc]);

  const negate = useCallback(() => {
    if (err) return;
    setCur(c => (c === '0' ? c : c.startsWith('-') ? c.slice(1) : '-' + c));
  }, [err]);

  const backspace = useCallback(() => {
    if (err || fresh) return;
    setCur(c => {
      const n = c.slice(0, -1);
      return n === '' || n === '-' ? '0' : n;
    });
  }, [err, fresh]);

  const press = useCallback((k) => {
    if (/^[0-9]$/.test(k)) digit(k);
    else if (k === ',' || k === '.') dot();
    else if (OPS[k]) applyOp(k);
    else if (k === '=') equals();
    else if (k === '%') percent();
    else if (k === '±') negate();
    else if (k === '⌫') backspace();
    else if (k === 'C') reset();
  }, [digit, dot, applyOp, equals, percent, negate, backspace, reset]);

  /* Klavye desteği — modal açıkken */
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable]')) return;
      const k = e.key;
      if (/^[0-9]$/.test(k)) { e.preventDefault(); press(k); }
      else if (k === ',' || k === '.') { e.preventDefault(); press(','); }
      else if (k === '+' || k === '-' || k === '*' || k === '/' || k === 'x') { e.preventDefault(); press(k === 'x' ? '*' : k); }
      else if (k === 'Enter' || k === '=') { e.preventDefault(); press('='); }
      else if (k === '%') { e.preventDefault(); press('%'); }
      else if (k === 'Backspace') { e.preventDefault(); press('⌫'); }
      else if (k === 'Delete') { e.preventDefault(); press('C'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press]);

  const copy = () => {
    if (err) return;
    try {
      navigator.clipboard.writeText(fmtEntry(cur));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  const shown = err ? 'Hata' : (fresh && cur !== '0' ? fmtNum(parseFloat(cur) || 0) : fmtEntry(cur));

  return (
    <div className="ftc">
      <div className="ftc-display" onClick={copy} title="Sonucu kopyala">
        {copied && <span className="ftc-copied">Kopyalandı ✓</span>}
        <div className="ftc-expr">{expr}&nbsp;</div>
        <div className={`ftc-value${err ? ' ftc-err' : ''}`}>{shown}</div>
      </div>
      <div className="ftc-grid">
        {KEYS.map(({ k, cls }) => (
          <button key={k} className={`ftc-key ${cls || ''}`} onClick={() => press(k)}>{k}</button>
        ))}
      </div>
      <div className="ftc-hint">Klavye ile de kullanılır · sonuca tıkla → kopyala</div>
    </div>
  );
};

export default CalculatorTool;
